import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { QUERY_CMS_PROPERTY_LANDLORD_CONTRACT } from '~pages/dashboard/properties/record/landlord-contract/queries';
import PropTypes from 'prop-types';
import { Row, Col, Spin, Icon, Empty } from 'antd';
import classNames from 'classnames';
import ContractFileList from '~components/contract-file-list';
import { showEffectiveToDate, calContractStatus } from '~helpers/contract';
import { contractStatus } from '~constants';
import { dayFormatType, formatDay } from '~helpers/day';
import { useTranslation } from 'react-i18next';
import generatePath from '~settings/routing';
import { Link } from 'react-router-dom';
import { landlordDetailTabs } from '~constants/landlord';
import { PropertyContractLandlord as PropertyContractIcon } from 'common/components/svgs';

export default function LandlordContract(props) {
  const { property } = props;
  const { t } = useTranslation();

  const { data: landlordContract, loading: getLandlordContract } = useQuery(
    QUERY_CMS_PROPERTY_LANDLORD_CONTRACT,
    {
      variables: {
        slug: property.slug,
      },
      skip: !property.slug,
    },
  );

  const contract = useMemo(() => {
    return landlordContract?.property.landlord.contracts.edges.map(edge => edge.node)[0] || null;
  }, [landlordContract]);

  const status = useMemo(() => {
    return calContractStatus(contract?.effectiveFrom, contract?.effectiveTo);
  }, [contract]);

  return (
    <div className="property-contract-landlord-container">
      <div className="property-contract-landlord__header">
        <h1 className="property-contract-landlord__header__title">
          {t('cms.property.record_landlord.landlord_level.title')}
        </h1>
      </div>
      <If condition={!contract}>
        <Spin spinning={getLandlordContract}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <p className="property-contract-landlord__blank-tip">
                {t('cms.property.record_landlord.landlord_level.blank')}.
              </p>
            }
          />
        </Spin>
      </If>
      <If condition={contract}>
        <div className="property-contract-landlord__content">
          <div className="property-contract-landlord__icon">
            <PropertyContractIcon />
          </div>
          <div className="property-contract-landlord">
            <span
              className={classNames('property-contract-landlord__status', {
                'property-contract-landlord__status--active': status === contractStatus.ACTIVE,
                'property-contract-landlord__status--inactive': status === contractStatus.INACTIVE,
                'property-contract-landlord__status--expired': status === contractStatus.EXPIRED,
              })}
            >
              {t(`cms.contract.status.${status.toLowerCase()}`)}
            </span>

            <div className="property-contract-landlord__content-container">
              <div className="property-contract-landlord__block">
                <p className="property-contract-landlord__block-title">
                  {t('cms.property.record_landlord.contract_title')}
                </p>

                <Row className="property-contract-landlord__info-row">
                  <Col span={7} className="property-contract-landlord__info-label">
                    {t('cms.contract.detail.info_label.signed_date')}
                  </Col>
                  <Col span={17} className="property-contract-landlord__info-content">
                    {formatDay(dayFormatType.UKFORMAT, contract.signedDate)}
                  </Col>
                </Row>
                <Row className="property-contract-landlord__info-row">
                  <Col span={7} className="property-contract-landlord__info-label">
                    {t('cms.contract.detail.info_label.effective_date')}
                  </Col>
                  <Col span={17} className="property-contract-landlord__info-content">
                    {`${formatDay(
                      dayFormatType.UKFORMAT,
                      contract.effectiveFrom,
                    )} ~ ${showEffectiveToDate(contract.effectiveTo)}`}
                  </Col>
                </Row>
              </div>

              <div className="property-contract-landlord__block">
                <p className="property-contract-landlord__block-title--small">
                  {t('cms.property.record_landlord.contracts_bind_to_landlord')}
                </p>
                <div className="property-contract-landlord__contract-list">
                  <ContractFileList files={contract.files} />
                </div>
              </div>
            </div>
            <div className="property-contract-landlord__footer-container">
              <p className="reference-and-contact__contact-section__tip">
                <Icon type="info-circle" />
                {t('cms.property.record_landlord.cant_edit.tip')}.
                <Link
                  to={generatePath(
                    'landlord',
                    { landlordSlug: landlordContract?.property.landlord?.slug },
                    {
                      tab: landlordDetailTabs.CONTRACT,
                    },
                  )}
                >
                  <span className="reference-and-contact__link">
                    {t('cms.reference_and_contact.cant_edit.link')}
                  </span>
                </Link>
              </p>
            </div>
          </div>
        </div>
      </If>
    </div>
  );
}

LandlordContract.prototypes = {
  property: PropTypes.object,
};

LandlordContract.defaultProps = {
  property: {},
};
